import { Button, Divider, Snackbar, Stack, Typography } from '@mui/material';
import { VyIcon } from '@vy/ui-design-system';
import React from 'react';
import type { FieldPath, FieldValues } from 'react-hook-form';
import { ErrorNotificationContainer } from './error-notification-components/ErrorNotificationContainer';
import type { ErrorNotificationMessages } from './error-notification-types/error-notification-messages';
import { isErrorNotificationDescriptor } from './utils/is-error-notification-descriptor';

type ErrorNotificationProps<FormInput extends FieldValues | undefined> = {
  errors: ErrorNotificationMessages<FormInput>;
  description?: React.ReactNode;
  onGoTo?: FormInput extends FieldValues ? (fieldPath: FieldPath<FormInput>) => void : never;
  open: boolean;
  onClose?: () => void;
};

export function ErrorNotification<FormInput extends FieldValues | undefined>({
  errors,
  description,
  onGoTo,
  open,
  onClose,
}: ErrorNotificationProps<FormInput>) {
  return (
    <Snackbar
      open={open}
      sx={{
        marginBottom: '70px',
      }}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      autoHideDuration={6000}
      onClose={onClose}
    >
      <ErrorNotificationContainer>
        <Stack
          direction="row"
          sx={{
            columnGap: "12px",
            alignItems: "center"
          }}>
          <VyIcon icon="WarningFilled" color="#EF5350" className="[&>*]:stroke-none" />

          <Stack>
            <Typography
              variant="h6"
              sx={{
                fontSize: "20px",
                color: "text.primary",
                fontWeight: 400
              }}>
              {`We've found ${errors.length} Errors`}
            </Typography>
            <Typography variant="body2" sx={{
              fontWeight: 300
            }}>
              {description}
            </Typography>
          </Stack>
        </Stack>

        <Divider sx={{ marginTop: '14px' }} />

        <Stack
          component="ul"
          divider={<Divider />}
          sx={{
            paddingY: "12px",
            rowGap: "12px"
          }}>
          {errors.map((error, index) => (
            <li key={crypto.randomUUID()}>
              <div className="flex items-center justify-between">
                <Typography
                  sx={{
                    fontSize: 14,
                    fontWeight: 300,

                    '&:first-letter': {
                      fontWeight: 400,
                      marginRight: '4px',
                    }
                  }}>
                  {`${index + 1} ${error.message}`}
                </Typography>

                {!isErrorNotificationDescriptor(error) && (
                  <Button
                    variant="text"
                    onClick={() => onGoTo?.(error.fieldPath)}
                    sx={{
                      padding: 0,
                      columnGap: '4px',
                    }}
                  >
                    Go To
                    <VyIcon
                      icon="ArrowSmallRight"
                      style={{
                        marginRight: '-10px',
                      }}
                    />
                  </Button>
                )}
              </div>
            </li>
          ))}
        </Stack>
      </ErrorNotificationContainer>
    </Snackbar>
  );
}
